import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { featureDescription, featureCards } from '../data/mockData';
import { ArrowRight } from 'lucide-react';

const FeatureDescriptionText = () => {
  const ref = useRef(null);
  const [visibleWords, setVisibleWords] = useState(0);
  const words = featureDescription.split(' ');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            let count = 0;
            const interval = setInterval(() => {
              count += 1;
              setVisibleWords(count);
              if (count >= words.length) clearInterval(interval);
            }, 40);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [words.length]);

  return (
    <div ref={ref} className="max-w-[1121px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
      {/* Feature description: DM Sans, 40px, weight 300, tracking -0.44px */}
      <h2 className="text-center text-[24px] sm:text-[32px] lg:text-[40px] font-light leading-[1.15] tracking-[-0.011em] text-[#0A3900]">
        {words.map((word, i) => (
          <span
            key={i}
            className="transition-opacity duration-300"
            style={{ opacity: i < visibleWords ? 1 : 0.1 }}
          >
            {word}{' '}
          </span>
        ))}
      </h2>
    </div>
  );
};

/* TEMPORARY: Videos sourced from alex.com as placeholders - replace with original videos before production */
const FeatureVideo = ({ card }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (videoRef.current) {
              videoRef.current.play().catch(() => {});
            }
          } else {
            if (videoRef.current) {
              videoRef.current.pause();
            }
          }
        });
      },
      { threshold: 0.3 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full h-full overflow-hidden relative">
      {/* Fallback image shown while video loads */}
      <img
        src={card.image}
        alt={card.title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        playsInline
        loop
        preload="none"
      >
        <source src={card.video} type="video/mp4" />
      </video>
    </div>
  );
};

const FeatureCard = ({ card, index }) => {
  const isEven = index % 2 === 0;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`rounded-2xl overflow-hidden transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className={`flex flex-col lg:flex-row min-h-[400px] lg:min-h-[480px] ${
        !isEven ? 'lg:flex-row-reverse' : ''
      }`}>
        {/* Text Side */}
        <div className="lg:w-1/2 bg-[#EAE5D9] p-8 lg:p-12 xl:p-16 flex flex-col justify-center">
          {/* Label: 16px, weight 500 */}
          <span className="text-[#0A3900] text-[14px] lg:text-[16px] font-medium leading-[24px] mb-3">
            {card.label}
          </span>
          {/* Heading: 40px, weight 400, tracking -0.44px */}
          <h3 className="text-[#0A3900] text-[28px] lg:text-[40px] font-normal leading-[1.15] tracking-[-0.011em] mb-5">
            {card.title}
          </h3>
          {/* Description: 16px, weight 500 */}
          <p className="text-[#0A3900] text-[14px] lg:text-[16px] font-medium leading-[24px] mb-6">
            {card.description}
          </p>
          {/* Link: 16px, weight 600 */}
          <Link
            to={card.linkUrl}
            className="inline-flex items-center gap-2 text-[#0A3900] text-[14px] lg:text-[16px] font-semibold leading-[24px] hover:gap-3 transition-all duration-300 group"
          >
            {card.linkText}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Video Side - TEMPORARY: Replace with original videos before production */}
        <div className="lg:w-1/2 bg-[#B8C4AD] flex items-center justify-center relative overflow-hidden">
          <FeatureVideo card={card} />
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  return (
    <section className="bg-[#F9FBF5]">
      <FeatureDescriptionText />

      {/* Feature Cards */}
      <div className="max-w-[1121px] mx-auto px-6 lg:px-10 pb-16 lg:pb-24">
        <div className="flex flex-col gap-8">
          {featureCards.map((card, i) => (
            <FeatureCard key={i} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
